import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import type { CarrierPrice } from "~/shared/schemas/carrier-schema.ts";
import type { QuoteStateType } from "~/shared/types/quote-details-types.ts";

import { AddCarrier } from "./components/add-carrier.tsx";
import { AddInsurance } from "./components/add-insurance.tsx";
import { MarginAdjustment } from "./components/margin-adjustment.tsx";
import { RateConfirmation } from "./components/rate-confirmation.tsx";

type CarrierSelectionHeaderProps = {
	adHocCarrierExists: boolean;
	addBlankCarrier: () => void;
	handleAdjustClick: (quoteState: QuoteStateType) => void;
	handleMarginTypeChange: (type: "percentage" | "flat") => void;
	handleRemoveInsuranceClick: () => void;
	isAddInsuranceEnabled: boolean;
	isAdjustingPrices: boolean;
	isRemovingInsurance: boolean;
	isSaveQuoteEnabled: boolean;
	isUpdatingMargins: boolean;
	marginType: "percentage" | "flat";
	marginValue: number;
	quoteState: QuoteStateType;
	setMarginValue: (value: number) => void;
	toggleModal: (modal: string) => void;
	updateAllCarrierMargins: (
		quoteId: number,
		eTag: string,
		carriers: CarrierPrice[],
	) => void;
};

export const CarrierSelectionHeader = ({
	adHocCarrierExists,
	addBlankCarrier,
	handleAdjustClick,
	handleMarginTypeChange,
	handleRemoveInsuranceClick,
	isAddInsuranceEnabled,
	isAdjustingPrices,
	isRemovingInsurance,
	isSaveQuoteEnabled,
	isUpdatingMargins,
	marginType,
	marginValue,
	quoteState,
	setMarginValue,
	toggleModal,
	updateAllCarrierMargins,
}: Readonly<CarrierSelectionHeaderProps>) => {
	const hasCarriers = quoteState.carrierPrices.length > 0;
	const { isInternal } = useDeploymentType();

	return (
		<div className={"carrier-selection-header"}>
			{isInternal ? (
				<>
					<MarginAdjustment
						handleAdjustClick={handleAdjustClick}
						handleMarginTypeChange={handleMarginTypeChange}
						isAdjustingPrices={isAdjustingPrices}
						marginType={marginType}
						marginValue={marginValue}
						quoteState={quoteState}
						setMarginValue={setMarginValue}
					/>
					<div className={"header-buttons"}>
						<RateConfirmation
							hasCarriers={hasCarriers}
							isAdjustingPrices={isAdjustingPrices}
							isInternal={isInternal}
							isSaveQuoteEnabled={isSaveQuoteEnabled}
							isUpdatingMargins={isUpdatingMargins}
							quoteState={quoteState}
							toggleModal={toggleModal}
							updateAllCarrierMargins={updateAllCarrierMargins}
						/>
						<AddCarrier
							adHocCarrierExists={adHocCarrierExists}
							addBlankCarrier={addBlankCarrier}
							isInternal={isInternal}
						/>
					</div>
					<div className={"add-insurance-wrapper"}>
						<AddInsurance
							handleRemoveInsuranceClick={handleRemoveInsuranceClick}
							hasCarriers={hasCarriers}
							isAddInsuranceEnabled={isAddInsuranceEnabled}
							isRemovingInsurance={isRemovingInsurance}
							quoteState={quoteState}
							toggleModal={toggleModal}
						/>
					</div>
				</>
			) : (
				<div className={"header-buttons external-header-buttons"}>
					<RateConfirmation
						hasCarriers={hasCarriers}
						isAdjustingPrices={isAdjustingPrices}
						isInternal={isInternal}
						isSaveQuoteEnabled={isSaveQuoteEnabled}
						isUpdatingMargins={isUpdatingMargins}
						quoteState={quoteState}
						toggleModal={toggleModal}
						updateAllCarrierMargins={updateAllCarrierMargins}
					/>
					<AddCarrier
						adHocCarrierExists={adHocCarrierExists}
						addBlankCarrier={addBlankCarrier}
						isInternal={isInternal}
					/>
					<AddInsurance
						handleRemoveInsuranceClick={handleRemoveInsuranceClick}
						hasCarriers={hasCarriers}
						isAddInsuranceEnabled={isAddInsuranceEnabled}
						isRemovingInsurance={isRemovingInsurance}
						quoteState={quoteState}
						toggleModal={toggleModal}
					/>
				</div>
			)}
		</div>
	);
};
